/**
 * EditorTypes — Shared constants, events, and types for the Cinematic Editor.
 */
import type { CinematicSequence } from '../CinematicTypes';

// ── Layout constants (1200x800 canvas) ──

export const TOOLBAR_H = 44;
export const TIMELINE_H = 120;
export const STEP_PANEL_W = 280;
export const CANVAS_W = 1200;
export const CANVAS_H = 800;

export const WORK_AREA = {
  x: 0,
  y: TOOLBAR_H,
  w: CANVAS_W - STEP_PANEL_W,
  h: CANVAS_H - TOOLBAR_H - TIMELINE_H,
};

// ── Editor event keys ──

export const EditorEvents = {
  STEP_SELECTED:   'cinEditor:stepSelected',
  STEP_ADDED:      'cinEditor:stepAdded',
  STEP_REMOVED:    'cinEditor:stepRemoved',
  STEP_UPDATED:    'cinEditor:stepUpdated',
  STEPS_REORDERED: 'cinEditor:stepsReordered',
  CAPTURE_START:   'cinEditor:captureStart',
  CAPTURE_DONE:    'cinEditor:captureDone',
  PREVIEW_START:   'cinEditor:previewStart',
  PREVIEW_STOP:    'cinEditor:previewStop',
  SEQUENCE_SWITCHED: 'cinEditor:sequenceSwitched',
  /** Single-step preview: play only the selected step */
  PREVIEW_SINGLE_STEP: 'cinEditor:previewSingleStep',
  /** Undo/redo counts changed — toolbar should update display */
  HISTORY_CHANGED: 'cinEditor:historyChanged',
  /** Toggle help overlay */
  HELP_TOGGLE:     'cinEditor:helpToggle',
} as const;

// ── Editor mode ──

export type EditorMode = 'edit' | 'captureCamera' | 'captureClick' | 'preview';

// ── Step type display metadata ──

export interface StepTypeMeta {
  type: string;
  label: string;
  color: number;
  icon: string;
}

// ── Shared mutable state passed to all sub-components ──

export interface EditorState {
  sequence: CinematicSequence;
  selectedStepIndex: number;
  /** Index of the step currently being played during preview (-1 when idle) */
  playingStepIndex: number;
  mode: EditorMode;
  /** The scene being overlaid (GameScene, LevelTreeScene, WorkshopScene, etc.) */
  hostScene: Phaser.Scene | null;
  /** Scene key string for pause/resume operations */
  hostSceneKey: string;
  /** All scenes to pause/resume when editor opens/closes */
  callerScenes: string[];
}
